import { defineComponent as _defineComponent } from 'vue'
import { analyzeLayout } from '@/apis/work-shop'
import GDragImage from '@/components/common/g-drag-image.vue'
import { From } from '@/data/constant'
import { BujianStoreItem } from '@/store/modules/bujianStore'
import { calculateImageDisplaySize } from '@/util/plankCommonFuncs'
import { matchHighlightBoardName } from '@/util/commonFun'
import { getPlankImage } from '@/views/newPaiban/component/m-paiban-store/utlis'
import { bujianStoreDragStrategy } from '@/views/newPaiban/component/m-paiban-store/utlis/storeDragStrategy'
import { useStoreDragPlank } from '@/views/newPaiban/component/m-paiban-store/utlis/useStoreDragPlank'
import { debounce } from 'lodash-es'
import { nextTick, ref, watch } from 'vue'


export default /*#__PURE__*/_defineComponent({
  __name: 'index',
  props: {
    data: { default: () => [] },
    loading: { type: Boolean, default: false },
    defaultScale: { default: 6 },
    scale: { default: 1 },
    plankLineWidth: { default: 1 },
    keyword: { default: '' }
  },
  emits: ["onSearch"],
  setup(__props: any, { expose, emit }: { emit: ({
  (e: 'onSearch', value: string): void
}), expose: any, slots: any, attrs: any }) {

const props = __props as { data: any[], loading: boolean, defaultScale: number, scale: number, plankLineWidth: number, keyword: string }





const BATCH_SIZE = 20

const keyword = ref(props.keyword)

watch(
  () => props.keyword,
  (val) => {
    if (val !== undefined && val !== keyword.value) {
      keyword.value = val
    }
  }
)

const bujianStoreRef = ref<HTMLElement | null>(null)

const handleSearch = debounce(() => {
  emit('onSearch', keyword.value)
}, 500)

const contentRef = ref<HTMLElement | null>(null)
const displayCount = ref(BATCH_SIZE)
const displayData = ref<any[]>([])
const preloadedIds = new Map<string, BujianStoreItem>()
const preloadLoading = ref(false)
let preloadTaskCount = 0

function updateDisplayData() {
  displayData.value = [...props.data.slice(0, displayCount.value)]
}

watch(
  () => props.data,
  () => {
    displayCount.value = BATCH_SIZE
    updateDisplayData()
    nextTick(() => {
      preloadVisibleItems(props.data.slice(0, BATCH_SIZE))
    })
  },
  { immediate: true }
)

watch(displayCount, (newCount, oldCount) => {
  if (newCount <= (oldCount ?? 0)) return
  updateDisplayData()
  const newItems = props.data.slice(oldCount ?? 0, newCount)
  if (!newItems.length) return
  nextTick(() => {
    preloadVisibleItems(newItems)
  })
})

function onScroll() {
  const el = contentRef.value
  if (!el) return
  const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 30
  if (nearBottom && displayCount.value < props.data.length) {
    displayCount.value = Math.min(
      displayCount.value + BATCH_SIZE,
      props.data.length
    )
  }
}

async function preloadVisibleItems(items: BujianStoreItem[]) {
  const itemsToPreload = items.filter(
    (item) => item.from !== From.BLUEN && item.id && !item._preloadedPlank
  )

  if (!itemsToPreload.length) return

  const hasPreload = itemsToPreload.filter((item) =>
    preloadedIds.has(String(item.id))
  )

  // 处理搜索命中缓存的情况
  hasPreload.forEach((item) => {
    const preloadedItem = preloadedIds.get(String(item.id))
    if (!preloadedItem) return
    item._preloadedPlank = preloadedItem._preloadedPlank
    item.url = preloadedItem.url
    item.width = preloadedItem.width
    item.height = preloadedItem.height
  })

  const notPreload = itemsToPreload.filter(
    (item) => !preloadedIds.has(String(item.id))
  )

  preloadTaskCount++
  preloadLoading.value = true
  try {
    const res = await analyzeLayout(notPreload.map((item) => item.oriData))
    if (res.status !== 1) throw new Error('analyzeLayout failed')
    const data = res.data.plank_data
    for (const [index, plank] of data.entries()) {
      const item = notPreload[index]
      if (!plank || !item) continue
      item._preloadedPlank = plank
      try {
        const imageUrl = getPlankImage(plank, false, {
          defaultScale: 6,
          scale: 3,
          plankLineWidth: 1,
        })
        if (imageUrl) {
          item.url = imageUrl
          const displaySize = await calculateImageDisplaySize(imageUrl)
          if (displaySize) {
            item.width = displaySize.width
            item.height = displaySize.height
          }
        }
        preloadedIds.set(String(item.id), item)
      } catch (_e) {}
    }
    updateDisplayData()
  } catch (_e) {
    itemsToPreload.forEach((item) => {
      preloadedIds.delete(String(item.id))
    })
  } finally {
    preloadTaskCount--
    if (preloadTaskCount <= 0) {
      preloadTaskCount = 0
      preloadLoading.value = false
    }
  }
}

const {
  getCurrentDragTarget,
  handleDragClick: handleStoreDragClick,
  handleTargetPositionChange,
} = useStoreDragPlank({
  getImageOptions() {
    return {
      defaultScale: props.defaultScale,
      scale: props.scale,
      plankLineWidth: props.plankLineWidth,
    }
  },
  currentDragMutation: {
    setMutation: 'bujianStore/setCurrentDragBujianPlank',
    updateMutation: 'bujianStore/updateCurrentDragBujianPlank',
  },
  ...bujianStoreDragStrategy,
})

function awaitHighGlossName(title: string) {
  return title.replace(
    '高光_',
    `${matchHighlightBoardName(extractMaterialName(title), false)}_`
  )
}

/**
 * 提取材料名称
 * @param {string} str - 原始字符串，例如 "18.9高光_多层实木(T01)"
 * @returns {string} - 返回中间的材料部分，例如 "多层实木"
 */
function extractMaterialName(str: string) {
  if (typeof str !== 'string') return ''
  const match = str.match(/_(.+?)(?:\(|$)/)
  return match ? match[1].trim() : ''
}

async function handleDragClick(
  data: any,
  _e: MouseEvent,
  item: any,
  options: any
) {
  await handleStoreDragClick(data, item, options)
}

function autoScrollToWaitedCompositionZone() {
  bujianStoreRef.value?.scrollIntoView({
    behavior: 'smooth',
    block: 'end',
    inline: 'nearest',
  })
}

expose({
  autoScrollToWaitedCompositionZone,
  getCurrentDragTarget,
})

return { __sfc: true,props, emit, BATCH_SIZE, keyword, bujianStoreRef, handleSearch, contentRef, displayCount, displayData, preloadedIds, preloadLoading, preloadTaskCount, updateDisplayData, onScroll, preloadVisibleItems, getCurrentDragTarget, handleStoreDragClick, handleTargetPositionChange, awaitHighGlossName, extractMaterialName, handleDragClick, autoScrollToWaitedCompositionZone, GDragImage }
}

})